





































































import { Component, Vue, Ref, Model } from "vue-property-decorator";
import { firestore } from "firebase";

import { TimelineVisibleArea } from "@/data/models/TimelineSettings";
import {
  getHoursAndMinutesFromTimestamp,
  getTimestampForHourAndMinutes,
} from "@/utils/DateHelper";

import {
  getTaskTypeColor,
  PlanningBoardSettings,
} from "@/data/models/SchedulingSettings";
import {
  TaskType,
  localiseTaskType,
  getTaskTypeFieldName,
} from "@/data/models/Task";

import PickerDialogComponent from "@/components/common/PickerDialog.vue";
import TimePickerComponent from "@/components/common/TimePicker.vue";
import ColorPickerDialogComponent from "@/components/common/ColorPickerDialog.vue";

@Component({
  components: {
    "time-picker": TimePickerComponent,
    "color-picker": ColorPickerDialogComponent,
    picker: PickerDialogComponent,
  },
})
export default class PlanningBoardSettingsComponent extends Vue {
  @Ref("colorPicker")
  colorPicker!: ColorPickerDialogComponent;

  visibleAreaOptions = [
    TimelineVisibleArea.ONE_DAY,
    TimelineVisibleArea.ONE_WEEK,
    TimelineVisibleArea.TWO_WEEKS,
    TimelineVisibleArea.FOUR_WEEKS,
  ];

  taskTypes = [
    TaskType.LEAK_DETECTION,
    TaskType.DAMAGE_ASSESSMENT,
    TaskType.DRYING_SETUP,
    TaskType.INTERMEDIATE_MEASUREMENT,
    TaskType.DRYING_CONCLUSION,
    TaskType.REPAIR,
  ];

  @Model("change")
  planningBoardSettings!: PlanningBoardSettings;

  get visibleArea() {
    return this.planningBoardSettings.visibleArea;
  }

  set visibleArea(visibleArea: TimelineVisibleArea) {
    this.updatePlanningBoardSettings((planningBoardSettings) => {
      planningBoardSettings.visibleArea = visibleArea;
    });
  }

  groupedTaskTypes = this.calculateGroupedLabels(this.taskTypes);

  calculateGroupedLabels(taskTypes: TaskType[]) {
    const groupSize = 3;
    const result: TaskType[][] = [];

    for (let i = 0; i < taskTypes.length; i += groupSize) {
      result.push(taskTypes.slice(i, i + groupSize));
    }

    return result;
  }

  getTaskTypeStyle(taskType: TaskType) {
    return {
      background: getTaskTypeColor(taskType, this.planningBoardSettings),
    };
  }

  localiseTaskType(taskType: TaskType) {
    return localiseTaskType(this, taskType);
  }

  get visibleAreaOfDayStart() {
    return getTimestampForHourAndMinutes(
      this.planningBoardSettings.visibleAreaOfDayStart
    );
  }

  set visibleAreaOfDayStart(timestamp: firestore.Timestamp) {
    this.updatePlanningBoardSettings((planningBoardSettings) => {
      planningBoardSettings.visibleAreaOfDayStart = getHoursAndMinutesFromTimestamp(
        timestamp
      );
    });
  }

  get visibleAreaOfDayEnd() {
    return getTimestampForHourAndMinutes(
      this.planningBoardSettings.visibleAreaOfDayEnd
    );
  }

  set visibleAreaOfDayEnd(timestamp: firestore.Timestamp) {
    this.updatePlanningBoardSettings((planningBoardSettings) => {
      planningBoardSettings.visibleAreaOfDayEnd = getHoursAndMinutesFromTimestamp(
        timestamp
      );
    });
  }

  async onChangeTaskTypeColor(taskType: TaskType) {
    const fieldName = getTaskTypeFieldName(taskType);
    const color = this.planningBoardSettings.taskColors[fieldName];
    const title = this.$t("settings.scheduling.planningBoard.formatColor", [
      localiseTaskType(this, taskType),
    ]).toString();

    const newColor = await this.colorPicker.show(title, color);

    if (newColor) {
      const newTaskColors = {
        ...this.planningBoardSettings.taskColors,
        [fieldName]: newColor,
      };

      this.updatePlanningBoardSettings((planningBoardSettings) => {
        planningBoardSettings.taskColors = newTaskColors;
      });
    }
  }

  localiseTimelineVisibleArea(timelineVisibleArea: TimelineVisibleArea) {
    switch (timelineVisibleArea) {
      case TimelineVisibleArea.ONE_DAY:
        return this.$t("settings.scheduling.planningBoard.visibleArea.oneDay");
      case TimelineVisibleArea.ONE_WEEK:
        return this.$t("settings.scheduling.planningBoard.visibleArea.oneWeek");
      case TimelineVisibleArea.TWO_WEEKS:
        return this.$t(
          "settings.scheduling.planningBoard.visibleArea.twoWeeks"
        );
      case TimelineVisibleArea.FOUR_WEEKS:
        return this.$t(
          "settings.scheduling.planningBoard.visibleArea.fourWeeks"
        );
    }
  }

  updatePlanningBoardSettings(
    callback: (planningBoardSettings: PlanningBoardSettings) => void
  ) {
    const currentValue: PlanningBoardSettings = this.planningBoardSettings;
    callback(currentValue);

    const update: PlanningBoardSettings = {
      ...currentValue,
    };

    this.$emit("change", update);
  }
}
